import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Steps, Checkbox, Empty, Spin, Tag, Space, Button, message } from 'antd';
import { useLazyQuery, useMutation } from '@apollo/client';
import { PREVIEW_BILL_SEND } from '../../graphql/queries/settlement/billSendQueries';
import { SEND_INVOICE_EMAIL } from '../../graphql/mutations/settlement/revenueInvoiceMutations';
import type { FinanceContactType } from '../../graphql/types/settlement';

interface InvoiceSendModalProps {
  open: boolean;
  invoiceId: string;
  billId: string;
  invoiceNo?: string | null;
  onCancel: () => void;
  onSuccess: () => void;
}

/**
 * Invoice 推送弹窗。候选联系人复用「发送账单」的 previewBillSend(billId).financeContacts，
 * 用户勾选后经 sendInvoiceEmail 透传给 settlement-service。
 */
const InvoiceSendModal: React.FC<InvoiceSendModalProps> = ({
  open,
  invoiceId,
  billId,
  invoiceNo,
  onCancel,
  onSuccess,
}) => {
  const [current, setCurrent] = useState(0);
  const [contacts, setContacts] = useState<FinanceContactType[]>([]);
  const [selected, setSelected] = useState<string[]>([]);

  const [loadPreview, { loading: previewLoading }] = useLazyQuery(PREVIEW_BILL_SEND, {
    fetchPolicy: 'network-only',
  });
  const [sendInvoiceEmail, { loading: sendLoading }] = useMutation(SEND_INVOICE_EMAIL);

  useEffect(() => {
    if (!open) return;
    setCurrent(0);
    (async () => {
      try {
        const res = await loadPreview({ variables: { billId } });
        const candidateContacts: FinanceContactType[] = res.data?.previewBillSend?.financeContacts ?? [];
        setContacts(candidateContacts);
        setSelected(candidateContacts.map((c) => c.email).filter(Boolean) as string[]);
      } catch {
        setContacts([]);
        setSelected([]);
      }
    })();
  }, [open, billId, loadPreview]);

  const canNext = useMemo(() => selected.length > 0, [selected]);

  const handleConfirm = async () => {
    try {
      const selectedContacts = contacts.filter((c) => c.email && selected.includes(c.email));
      const recipients = selectedContacts.map((c) => ({
        customerName: c.name ?? null,
        email: c.email!,
      }));
      const res = await sendInvoiceEmail({ variables: { invoiceId, recipients } });
      const result = res.data?.sendInvoiceEmail;
      if (result?.success) {
        message.success('Invoice 已推送');
        onSuccess();
      } else {
        message.error(result?.message || 'Invoice 推送失败');
      }
    } catch (e) {
      message.error((e as Error)?.message || 'Invoice 推送失败');
    }
  };

  return (
    <Modal
      open={open}
      title="Invoice 推送"
      onCancel={onCancel}
      width={640}
      footer={null}
      destroyOnClose
    >
      <Steps
        current={current}
        className="mb-4"
        items={[{ title: '选择收件人' }, { title: '确认发送' }]}
      />

      {current === 0 && (
        <div>
          <div className="text-sm text-gray-600 mb-2">
            Invoice：<span className="font-medium">{invoiceNo || invoiceId}</span>
          </div>
          <div className="text-sm text-gray-600 mb-2">请选择收件人（默认全选，至少选择 1 个）：</div>
          <Spin spinning={previewLoading}>
            {contacts.length === 0 ? (
              <Empty description="客户无可用财务联系人" />
            ) : (
              <Checkbox.Group
                className="flex flex-col gap-2"
                value={selected}
                onChange={(v) => setSelected(v as string[])}
              >
                {contacts.map((c) => (
                  <Checkbox key={c.email} value={c.email}>
                    {c.name ? `${c.name}（${c.email}）` : c.email}
                  </Checkbox>
                ))}
              </Checkbox.Group>
            )}
          </Spin>
          <div className="mt-6 flex justify-end">
            <Space>
              <Button onClick={onCancel}>取消</Button>
              <Button type="primary" disabled={!canNext} onClick={() => setCurrent(1)}>
                下一步
              </Button>
            </Space>
          </div>
        </div>
      )}

      {current === 1 && (
        <div>
          <div className="text-sm text-gray-700 mb-3">
            确认将 Invoice <span className="font-medium">{invoiceNo || invoiceId}</span> 发送至以下收件人：
          </div>
          <div className="flex flex-wrap gap-2 mb-4">
            {contacts
              .filter((c) => c.email && selected.includes(c.email))
              .map((c) => (
                <Tag key={c.email} color="blue">
                  {c.name ? `${c.name}（${c.email}）` : c.email}
                </Tag>
              ))}
          </div>
          <div className="mt-6 flex justify-end">
            <Space>
              <Button onClick={() => setCurrent(0)}>上一步</Button>
              <Button type="primary" loading={sendLoading} onClick={handleConfirm}>
                确认发送
              </Button>
            </Space>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default InvoiceSendModal;
